/**
 * DEV-95 (caso B) — reconhecer "o chunk da rota não chegou".
 *
 * Quando o `import()` de um chunk de rota falha, o candidato caía em "Algo deu
 * errado / Tivemos um problema para carregar esta página", com um "Tentar
 * novamente" que chama o `reset()` do router — e o browser CACHEIA falha de
 * módulo no *module map*. Medido em prd (`app.eureca.me`, 27/08/2026): três
 * `import()` seguidos da mesma URL inexistente devolvem 245,7 ms / 1 request na
 * primeira tentativa e 0,1 ms / **0 requests** na segunda e na terceira. O botão
 * é inócuo por construção, para o resto da vida daquela página — nem quando a
 * rede volta ele recupera. Só um recarregamento sai do estado.
 *
 * Dois gatilhos alimentam o caso, e a classificação não depende de decidir qual
 * pesa mais: (a) implantação nova troca os hashes e a sessão viva (aba
 * restaurada no iOS Safari, aba de fundo por horas) guarda o module graph
 * antigo; (b) chunk grande em rede móvel simplesmente não termina de baixar.
 *
 * **A classificação NÃO pode se basear em status HTTP.** O fallback SPA do
 * Cloudflare Pages devolve `200` com `text/html` para asset ausente — medido:
 * `curl https://app.eureca.me/assets/_moduleId-AAAAAAAA.js` → `http=200
 * type=text/html; charset=utf-8`. O browser então falha ao PARSEAR HTML como
 * módulo; não existe 404 para observar. Sobra a mensagem do erro, que é o que
 * cada engine emite:
 *
 * | Engine          | Situação            | Mensagem                                                         |
 * |-----------------|---------------------|------------------------------------------------------------------|
 * | Chrome / Edge   | fetch ou MIME       | `Failed to fetch dynamically imported module: <url>`             |
 * | Firefox         | fetch ou MIME       | `error loading dynamically imported module`                      |
 * | Safari / iOS    | falha de REDE       | `Importing a module script failed.`                              |
 * | Safari / iOS    | MIME type inválido  | `'text/html' is not a valid JavaScript MIME type for module script '<url>'.` |
 *
 * **EU-747** — a última linha faltava. O WebKit (Safari e TODOS os browsers do
 * iOS) tem UMA mensagem para falha de rede e OUTRA para módulo servido com MIME
 * errado — que é exatamente o caso do fallback SPA (`200 text/html`). Sem ela,
 * o candidato no iPhone caía em "Algo deu errado" com o `reset()` inócuo,
 * enquanto Chrome e Firefox recarregavam sozinhos (card EU-747, prd
 * 09/09/2026). Fonte: WebKit `Source/WebCore/bindings/js/ScriptModuleLoader.cpp`
 * e `LayoutTests/http/tests/security/module-incorrect-mime-types-expected.txt`.
 *
 * Casar por substring é frágil por natureza, então há DUAS defesas: as
 * assinaturas abaixo (rápidas, sem estado) e o registro de erros do
 * `vite:preloadError` (`installChunkPreloadErrorRegistry`), agnóstico de
 * mensagem. O fallback continua sendo o lado seguro: não reconhecer uma
 * variante nova mantém o comportamento atual (tela de erro de servidor, cujo
 * retry a EU-747 fez escalar para recarregamento), nunca um reload em loop.
 */

/**
 * Assinaturas de falha de carregamento de MÓDULO. Minúsculas — a comparação é
 * case-insensitive, porque a capitalização varia entre engines e versões.
 */
const DYNAMIC_IMPORT_FAILURE_SIGNATURES = [
  // Chrome, Edge, Chrome Android — a maioria do tráfego do App Candidato.
  'failed to fetch dynamically imported module',
  // Firefox.
  'error loading dynamically imported module',
  // Safari / iOS Safari (todos os browsers do iOS usam este engine) — falha de rede.
  'importing a module script failed',
  // EU-747 — Safari / iOS: módulo servido com MIME type inválido (o `index.html`
  // do fallback SPA no lugar do chunk de uma implantação antiga).
  'is not a valid javascript mime type',
  // Vite injeta este prefixo no próprio handler de preload.
  'unable to preload css',
  'failed to fetch dynamically imported',
] as const;

/**
 * EU-747 — erros que o Vite viu falhar no `__vitePreload`. Registrados pelo
 * listener de `vite:preloadError` (ver `installChunkPreloadErrorRegistry`).
 * `WeakSet`: os objetos morrem com a página, sem vazar memória.
 */
const preloadErrors = new WeakSet<object>();
let preloadErrorListener: ((event: Event) => void) | null = null;

/**
 * `true` quando o erro é o chunk de uma rota que não chegou — e não um erro de
 * servidor. Consulta o registro do Vite (`vite:preloadError`), depois `message`,
 * `name` e a `cause` (o Vite embrulha o erro original no handler de preload).
 */
export function isChunkLoadFailure(error: unknown): boolean {
  if (wasSeenByVitePreload(error)) return true;
  return collectMessages(error).some((message) =>
    DYNAMIC_IMPORT_FAILURE_SIGNATURES.some((signature) => message.includes(signature))
  );
}

/** O erro (ou algo na sua cadeia de `cause`, profundidade máx. 3) passou pelo `vite:preloadError`. */
function wasSeenByVitePreload(error: unknown, depth = 0): boolean {
  if (depth > 3 || error === null || typeof error !== 'object') return false;
  if (preloadErrors.has(error)) return true;
  return wasSeenByVitePreload((error as { cause?: unknown }).cause, depth + 1);
}

/** Mensagens candidatas do erro e da sua cadeia de `cause` (profundidade máx. 3). */
function collectMessages(error: unknown, depth = 0): string[] {
  if (depth > 3 || error === null || error === undefined) return [];
  if (typeof error === 'string') return [error.toLowerCase()];
  if (typeof error !== 'object') return [];

  const candidate = error as { message?: unknown; name?: unknown; cause?: unknown };
  const messages: string[] = [];
  if (typeof candidate.message === 'string') messages.push(candidate.message.toLowerCase());
  if (typeof candidate.name === 'string') messages.push(candidate.name.toLowerCase());
  // O Vite embrulha o erro do `import()` no handler de preload de CSS.
  messages.push(...collectMessages(candidate.cause, depth + 1));
  return messages;
}

/**
 * EU-747 — rede agnóstica de engine para "o chunk não chegou".
 *
 * O helper `__vitePreload` do Vite (verificado no bundle de prd) dispara
 * `vite:preloadError` com `event.payload = err` e, se ninguém cancelar o
 * evento, **relança o MESMO objeto**. Esse objeto atravessa o
 * `lazyRouteComponent` do TanStack (que o guarda em closure) e chega intacto ao
 * `RouterErrorScreen` em `props.error`. Guardar a referência aqui classifica a
 * falha sem depender da mensagem — a próxima variante de engine já nasce
 * coberta, e a mensagem crua vai para a telemetria para virarmos assinatura.
 *
 * Deliberadamente este listener NÃO recarrega a página nem cancela o evento:
 *
 * - `defaultPreload: 'intent'` dispara o `import()` no hover/touchstart de um
 *   link. Recarregar nesse momento derrubaria um formulário em preenchimento
 *   sem o candidato ter navegado. Quem decide recarregar é o
 *   `RouterErrorScreen`, na navegação real, com o cooldown do DEV-95.
 * - `preventDefault()` faria o `import()` resolver `undefined` e o TanStack
 *   renderizar `createElement(undefined)` — um erro pior no lugar do original.
 *
 * Idempotente: chamadas repetidas não empilham listeners.
 */
export function installChunkPreloadErrorRegistry(): void {
  if (preloadErrorListener !== null || typeof window === 'undefined') return;
  preloadErrorListener = (event) => {
    const payload = (event as Event & { payload?: unknown }).payload;
    if (payload !== null && typeof payload === 'object') preloadErrors.add(payload);
  };
  window.addEventListener('vite:preloadError', preloadErrorListener);
}

/** Só para o teste — desinstala o listener para cada caso começar limpo. */
export function resetChunkPreloadErrorRegistryForTests(): void {
  if (preloadErrorListener !== null && typeof window !== 'undefined') {
    window.removeEventListener('vite:preloadError', preloadErrorListener);
  }
  preloadErrorListener = null;
}

/**
 * Marca de "já recarreguei por falha de chunk nesta aba", com o instante.
 *
 * `sessionStorage` e não `localStorage`: o escopo é a ABA. Duas abas na mesma
 * implantação quebrada devem cada uma ganhar seu recarregamento, e a marca não
 * pode sobreviver ao fechamento do browser.
 */
const RELOAD_MARK_KEY = 'eureca:chunk-reload-attempted';

/**
 * Quanto tempo a marca inibe um novo recarregamento automático.
 *
 * A marca EXPIRA em vez de valer para sempre, e nenhuma das duas alternativas
 * óbvias serve:
 *
 * - limpá-la no boot bem-sucedido do app é o loop de reload de volta — o app
 *   boota (o chunk do `index` está sempre lá), a marca some, o candidato navega,
 *   o mesmo chunk falha, recarrega, boota, limpa… indefinidamente;
 * - deixá-la valer pela sessão inteira tira o recarregamento automático de quem
 *   passou por uma implantação de manhã e volta ao app à tarde — sessão de
 *   candidato em mobile dura horas.
 *
 * O cooldown resolve os dois: duas falhas dentro da janela são a MESMA
 * ocorrência (o reload leva segundos, não minutos), então a segunda vê a tela; e
 * uma falha nova horas depois volta a ter direito ao recarregamento que resolve
 * o caso comum, que é implantação trocando os hashes.
 */
export const CHUNK_RELOAD_COOLDOWN_MS = 10 * 60_000;

/**
 * A primeira ocorrência recarrega sozinha; a segunda dentro da janela mostra a
 * tela.
 *
 * Devolve `true` quando ESTE chamador deve recarregar — e nesse caso já gravou a
 * marca. É o guard contra loop de reload: se o chunk continuar indisponível
 * depois do recarregamento, a segunda ocorrência encontra a marca fresca,
 * devolve `false`, e o candidato vê uma tela com copy própria em vez de a aba
 * entrar em ciclo infinito.
 *
 * `sessionStorage` indisponível (Safari privado, storage bloqueado) é tratado
 * como "já tentei": sem onde gravar a marca, recarregar seria exatamente o loop
 * que este guard existe para impedir.
 */
export function shouldReloadForChunkFailure(now: number = Date.now()): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  try {
    const previous = Number(sessionStorage.getItem(RELOAD_MARK_KEY));
    // `Number(null)` é `0` e `Number('lixo')` é `NaN`: os dois caem no ramo de
    // "sem marca válida", que é o lado certo — a primeira ocorrência recarrega.
    const withinCooldown =
      Number.isFinite(previous) && previous > 0 && now - previous < CHUNK_RELOAD_COOLDOWN_MS;
    if (withinCooldown) return false;
    sessionStorage.setItem(RELOAD_MARK_KEY, String(now));
    return true;
  } catch {
    return false;
  }
}

/** Só para o teste — a chave não deve ser reescrita à mão em outro lugar. */
export const CHUNK_RELOAD_MARK_KEY = RELOAD_MARK_KEY;
